import {
  getAnalytics,
  isSupported,
  setAnalyticsCollectionEnabled,
  setUserProperties,
} from "firebase/analytics";
import { getFirebaseApp } from "./firebase-app.js";
import { getBuildVariant, isFrvrBuildVariant } from "../core/player-stats.js";
import { getAppVersion } from "../config/app-version.js";

/** Firebase custom user property (matches event param `build_variant`). */
export const ANALYTICS_USER_PROPERTY_BUILD_VARIANT = "build_variant";
/** Firebase custom user property (matches event param `app_version`). */
export const ANALYTICS_USER_PROPERTY_APP_VERSION = "app_version";
/** Effective sky motion after auto probe or manual preference (`dynamic` / `static` / `auto_pending`). */
export const ANALYTICS_USER_PROPERTY_SKY_BG_MODE = "sky_bg_mode";
/** Player sky preference (`auto` / `dynamic` / `static`). */
export const ANALYTICS_USER_PROPERTY_SKY_BG_PREF = "sky_bg_pref";

/** @type {import("firebase/analytics").Analytics | null} */
var analyticsInstance = null;
/** @type {Promise<import("firebase/analytics").Analytics | null> | null} */
var initPromise = null;
/**
 * Store app: resolves with Google's consent answer (see `core/admob-consent.js`). Null on the web,
 * where builds keep their own rules.
 * @type {Promise<boolean> | null}
 */
var consentGate = null;

/**
 * @param {import("firebase/analytics").Analytics | null | undefined} analytics
 * @param {string} [buildVariant]
 * @param {string} [appVersion]
 */
export function applyAnalyticsUserProperties(analytics, buildVariant, appVersion) {
  if (!analytics) return;
  var variant = typeof buildVariant === "string" && buildVariant
    ? buildVariant
    : getBuildVariant();
  var version = typeof appVersion === "string" && appVersion
    ? appVersion
    : getAppVersion();
  setUserProperties(analytics, {
    build_variant: variant,
    app_version: version,
  });
}

/**
 * @param {Record<string, string | number | boolean | null | undefined>} props
 * @returns {Record<string, string>}
 */
export function formatPerfUserProperties(props) {
  var out = {};
  if (!props || typeof props !== "object") return out;
  var keys = Object.keys(props);
  for (var i = 0; i < keys.length; i++) {
    var key = keys[i];
    var value = props[key];
    if (value == null) continue;
    if (typeof value === "boolean") out[key] = value ? "1" : "0";
    else if (typeof value === "number" && isFinite(value)) out[key] = String(Math.floor(value));
    else out[key] = String(value);
  }
  return out;
}

/**
 * @param {import("firebase/analytics").Analytics | null | undefined} analytics
 * @param {Record<string, string | number | boolean | null | undefined>} perfProps
 */
export function applyPerfAnalyticsUserProperties(analytics, perfProps) {
  if (!analytics) return;
  var formatted = formatPerfUserProperties(perfProps);
  if (Object.keys(formatted).length === 0) return;
  try {
    setUserProperties(analytics, formatted);
  } catch (err) {
    console.warn("[analytics] perf user properties failed:", err);
  }
}

/**
 * Initialize Analytics and register default user properties as early as possible.
 * @param {{ forceEnable?: boolean }} [opts]
 * @returns {Promise<import("firebase/analytics").Analytics | null>}
 */
export async function bootstrapFirebaseAnalytics(opts) {
  return initFirebaseAnalytics(opts);
}

/**
 * Lazily initialize Firebase Analytics once per page load.
 * Returns null in non-browser environments, unsupported browsers, or on failure.
 * @param {{ forceEnable?: boolean }} [opts]
 * @returns {Promise<import("firebase/analytics").Analytics | null>}
 */
export function initFirebaseAnalytics(opts) {
  if (analyticsInstance) return Promise.resolve(analyticsInstance);
  if (initPromise) return initPromise;

  initPromise = (async function () {
    if (typeof window === "undefined") return null;
    if (!opts || !opts.forceEnable) {
      if (typeof import.meta !== "undefined" && import.meta.env && import.meta.env.MODE === "test") {
        return null;
      }
      var buildVariant = getBuildVariant();
      if (buildVariant === "yt"
        || buildVariant === "poki"
        || buildVariant === "gamedistribution"
        || isFrvrBuildVariant(buildVariant)) {
        return null;
      }
      if (typeof import.meta !== "undefined" && import.meta.env
        && import.meta.env.VITE_WITH_FRVR === "true") {
        return null;
      }
      if (typeof import.meta !== "undefined" && import.meta.env
        && import.meta.env.VITE_WITH_FIREBASE === "false") {
        return null;
      }
    }
    if (consentGate && !(await consentGate)) {
      // A later grant (privacy options form) may start Analytics this session.
      initPromise = null;
      return null;
    }
    try {
      var supported = await isSupported();
      if (!supported) return null;
      var app = getFirebaseApp();
      if (!app) return null;
      analyticsInstance = getAnalytics(app);
      applyAnalyticsUserProperties(analyticsInstance);
      return analyticsInstance;
    } catch (err) {
      console.warn("[analytics] Firebase init failed:", err);
      return null;
    }
  })();

  return initPromise;
}

/**
 * Store app: hold Analytics until Google's consent form has an answer. Events tracked meanwhile
 * wait in the tracker queue and are dropped if the answer is no.
 * @param {Promise<boolean>} answer
 */
export function requireAnalyticsConsent(answer) {
  consentGate = Promise.resolve(answer).then(Boolean, function () { return false; });
}

/**
 * Store app: the player changed their choice (privacy options form). Stops collection at once, or
 * starts Analytics when it never ran this session.
 * @param {boolean} allowed
 * @returns {Promise<import("firebase/analytics").Analytics | null>}
 */
export function setAnalyticsConsent(allowed) {
  consentGate = Promise.resolve(!!allowed);
  if (analyticsInstance) {
    try {
      setAnalyticsCollectionEnabled(analyticsInstance, !!allowed);
    } catch (err) {
      console.warn("[analytics] collection switch failed:", err);
    }
    return Promise.resolve(allowed ? analyticsInstance : null);
  }
  return allowed ? initFirebaseAnalytics() : Promise.resolve(null);
}

/** @returns {import("firebase/analytics").Analytics | null} */
export function getFirebaseAnalyticsInstance() {
  return analyticsInstance;
}

/** Test helper — reset singleton between tests. */
export function resetFirebaseAnalyticsForTests() {
  analyticsInstance = null;
  initPromise = null;
  consentGate = null;
}
